import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Vovy Extension Handoff Bridge
 *
 * 1. Sets a non-HttpOnly hint cookie `vovy_signed_in=1` on *.vovy.ai whenever
 *    the user has an active Supabase session. The extension reads this via
 *    chrome.cookies API to know it can request a handoff.
 * 2. Listens for window.postMessage from the extension's content script
 *    requesting a handoff token, calls ext-session-mint, and posts the result
 *    back. All messages are gated to *.vovy.ai origins.
 *
 * Safe no-op outside vovy.ai domains.
 */

const COOKIE_NAME = "vovy_signed_in";
const COOKIE_DOMAIN = ".vovy.ai";

function isVovyHost(): boolean {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h === "vovy.ai" || h.endsWith(".vovy.ai");
}

function setHintCookie(signedIn: boolean) {
  if (typeof document === "undefined" || !isVovyHost()) return;
  if (signedIn) {
    // 30 days, readable by extension. No sensitive data — just a boolean hint.
    const maxAge = 60 * 60 * 24 * 30;
    document.cookie =
      `${COOKIE_NAME}=1; Domain=${COOKIE_DOMAIN}; Path=/; Max-Age=${maxAge}; Secure; SameSite=Lax`;
  } else {
    document.cookie =
      `${COOKIE_NAME}=; Domain=${COOKIE_DOMAIN}; Path=/; Max-Age=0; Secure; SameSite=Lax`;
  }
}

export function useExtensionHandoff() {
  useEffect(() => {
    let mounted = true;

    // Sync cookie with current session and on auth changes.
    supabase.auth.getSession().then(({ data }) => {
      if (mounted) setHintCookie(!!data.session);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_evt, session) => {
      setHintCookie(!!session);
    });

    // postMessage bridge: only respond to same-origin (the page itself; the
    // extension content script runs in the page world or messages via the
    // page). We additionally require a known message type.
    async function onMessage(evt: MessageEvent) {
      if (evt.source !== window) return;
      if (evt.origin !== window.location.origin) return;
      const data = evt.data as
        | { type?: string; requestId?: string; browser?: string }
        | undefined;
      if (!data || data.type !== "VOVY_EXT_REQUEST_HANDOFF") return;

      try {
        const { data: sess } = await supabase.auth.getSession();
        if (!sess.session) {
          window.postMessage(
            {
              type: "VOVY_EXT_HANDOFF_RESPONSE",
              requestId: data.requestId,
              error: "not_signed_in",
            },
            window.location.origin,
          );
          return;
        }

        const { data: mintData, error } = await supabase.functions.invoke(
          "ext-session-mint",
          {
            headers: data.browser
              ? { "X-Vovy-Browser": data.browser }
              : undefined,
          },
        );

        if (error || !mintData?.handoff_token) {
          window.postMessage(
            {
              type: "VOVY_EXT_HANDOFF_RESPONSE",
              requestId: data.requestId,
              error: "mint_failed",
            },
            window.location.origin,
          );
          return;
        }

        window.postMessage(
          {
            type: "VOVY_EXT_HANDOFF_RESPONSE",
            requestId: data.requestId,
            handoff_token: mintData.handoff_token,
            expires_in: mintData.expires_in,
          },
          window.location.origin,
        );
      } catch (e) {
        console.warn("[vovy-ext-handoff] error", e);
      }
    }

    window.addEventListener("message", onMessage);

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
      window.removeEventListener("message", onMessage);
    };
  }, []);
}
